<template>
  <v-dialog v-model="dialog" persistent max-width="820px">
    <v-card class="delivery-dialog-card">
      <div class="delivery-dialog-header">
        <div>
          <div class="delivery-dialog-title">New Delivery</div>
          <div class="delivery-dialog-subtitle">
            Schedule delivery, pickup, or shipping for a customer.
          </div>
        </div>

        <v-btn icon small @click.stop.prevent="close()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <v-row dense>
          <v-col cols="12">
            <v-card outlined class="delivery-date-card">
              <div class="section-title-row">
                <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                <div>
                  <div class="section-title">Delivery Date & Time</div>
                  <div class="section-subtitle">
                    Select the delivery date, start time, and end time.
                  </div>
                </div>
              </div>

              <div class="mt-3">
                <DateTimeSelector
                  v-model="deliveryDate"
                  label="Delivery Date"
                  :resetKey="dateTimeResetKey"
                />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="8">
            <v-card outlined class="form-card">
              <div class="section-title mb-3">Delivery Details</div>

              <v-row dense>
                <v-col cols="12">
                  <CustomerAutoComplete
                    v-model="selectedCustomer"
                    @reset="$emit('reset')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-select
                    :items="Invoices"
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-magnify"
                    dense
                    :loading="loading"
                    auto-select-first
                    label="Select Invoice"
                    single-line
                    clearable
                    item-text="localId"
                    return-object
                    v-model="selectedInvoice"
                    class="rounded-input"
                  >
                    <template slot="item" slot-scope="{ item }">
                      <div>
                        <div class="font-weight-bold">Invoice #{{ item.localId }}</div>
                        <small>{{ item.date | moment("L LT") }}</small>
                      </div>

                      <v-spacer />

                      <span class="font-weight-bold">
                        {{ item.total | currency }}
                      </span>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model.trim="Delivery.name"
                    :error-messages="nameErrors"
                    :hide-details="nameErrors.length === 0"
                    outlined
                    @focus="$event.target.select()"
                    dense
                    label="Name"
                    ref="deliveryName"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="Delivery.deliveryType"
                    :items="deliveryTypes"
                    label="Delivery Type"
                    outlined
                    dense
                    hide-details
                    clearable
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                  v-if="Delivery.deliveryType === 'Shipping'"
                >
                  <v-text-field
                    v-model="Delivery.trackingNumber"
                    label="Tracking Number"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    counter="counter"
                    hide-details
                    outlined
                    dense
                    auto-grow
                    v-model="Delivery.note"
                    label="Description"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined class="day-delivery-card">
              <div class="section-title">Delivery on this day</div>
              <div class="section-subtitle mb-3">
                Existing deliveries for the selected date.
              </div>

              <div v-if="deliveryInfo && deliveryInfo.length">
                <v-list dense class="transparent pa-0">
                  <v-list-item
                    v-for="(delivery, index) in deliveryInfo"
                    :key="index"
                    class="delivery-existing-row"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        {{ delivery.name || "Unnamed Delivery" }}
                      </v-list-item-title>

                      <v-list-item-subtitle>
                        {{ delivery.deliveryDate | moment("MM/DD") }}
                        · {{ formatTime(delivery.startTime) }}
                        -
                        {{ formatTime(delivery.endTime) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>

              <div v-else class="empty-delivery-box">
                No delivery found for this date.
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="delivery-actions">
        <v-spacer />
        <v-btn rounded text @click="close()">Close</v-btn>
        <v-btn rounded color="primary" :loading="loading" dark @click="submit()">
          Save Delivery
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import GlobalFunction from "@/mixins/GlobalFunction";
import { mapGetters } from "vuex";
import CustomerAutoComplete from "@/modules/Customer/components/AutoComplete";
const { validationMixin } = require("vuelidate");
const { required, minLength, minValue } = require("vuelidate/lib/validators");
import InvoiceService from "@/modules/Invoice/service.js";
import deliveryService from "@/modules/Delivery/service.js";
import DateTimeSelector from "@/components/DateTimeSelector";

export default {
  props: {
    createDialog: {
      default: false,
    },
  },
  name: "create-delivery",
  data() {
    return {
      deliveryTypes: ["Shipping", "Pickup", "Delivery", "Pickup from Shop"],
      dateTimeResetKey: 0,
      deliveryDate: {},
      Delivery: {
        name: null,
        deliveryDate: null,
        startTime: null,
        endTime: null,
        note: null,
        InvoiceId: null,
        deliveryOrder: null,
        deliveryType: null,
        trackingNumber: null,
      },
      dialog: false,
      loading: false,
      deliveryInfo: null,
      Invoices: [],
      selectedInvoice: null,
      selectedCustomer: null,
    };
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    Delivery: {
      name: {
        required,
        minLength: minLength(4),
      },
      // CategoryId: {
      //   required,
      //   minValue: minValue(1),
      // },
    },
  },
  components: {
    CustomerAutoComplete,
    DateTimeSelector,
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    nameErrors() {
      const errors = [];
      if (!this.$v.Delivery.name.$dirty) return errors;
      if (!this.$v.Delivery.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      if (!this.$v.Delivery.name.minLength)
        errors.push("Field must be at least 4 characters long");
      return errors;
    },
  },
  watch: {
    deliveryDate(val) {
      if (val && val.date) {
        this.Delivery.deliveryDate = val.date;
        this.getDeliveryByDate(val.date); // fetch data when date is selected
      }
      if (val && val.startTime) {
        this.Delivery.startTime = val.startTime;
      }
      if (val && val.endTime) {
        this.Delivery.endTime = val.endTime;
      }
    },
    selectedInvoice(val) {
      if (val) {
        this.Delivery.name = `${this.selectedCustomer.name} | Inv#${val.localId}`;
        this.Delivery.InvoiceId = val.id;
      }
    },
    selectedCustomer(val) {
      if (val) {
        this.getInvoiceList();
        this.Delivery.name = `${this.selectedCustomer.name} `;
      }
    },
    createDialog(val) {
      this.dialog = val;
    },
  },
  methods: {
    formatTime(time) {
      if (!time) return "Not Set";
      return this.$moment(time, "HH:mm").format("hh:mm A");
    },
    getInvoiceList() {
      let where = {
        CustomerId: this.selectedCustomer.id,
        start: this.$moment()
          .startOf("days")
          .subtract(90, "days")
          .toISOString(),
      };

      this.loading = true;
      return InvoiceService.getAll(where).then((response) => {
        console.log("response", response);
        this.loading = false;
        this.Invoices = response.data;
      });
    },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.Delivery = {
        name: null,
        note: null,
        deliveryDate: null,
        startTime: null,
        endTime: null,
        InvoiceId: null,
        deliveryOrder: null,
        deliveryType: null,
        trackingNumber: null,
      };
      this.dateTimeResetKey = Date.now();
      this.deliveryInfo = null;
      this.Invoices = [];
      this.selectedInvoice = null;
      this.selectedCustomer = null;
      this.deliveryDate = {};
      this.$v.$reset();
    },
    getDeliveryByDate(date) {
      if (!date) return;

      // const startOfDay = this.$moment.utc(date).startOf("day").toISOString();
      // const endOfDay = this.$moment.utc(date).endOf("day").toISOString();

      const startOfDay = this.$moment
        .utc(date)
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss"); // '2025-05-30 00:00:00'
      const endOfDay = this.$moment
        .utc(date)
        .endOf("day")
        .format("YYYY-MM-DD 00:00:00"); // '2025-05-30 23:59:59'

      console.log("startOfDay", startOfDay);
      console.log("endOfDay", endOfDay);
      this.loading = true;
      deliveryService
        .getAll({ start: startOfDay, end: endOfDay })
        .then((response) => {
          this.deliveryInfo = response.data;

          // Set deliveryOrder based on how many Delivery exist already
          this.Delivery.deliveryOrder = (response.data.length || 0) + 1;

          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          console.error("Error fetching Delivery:", err);
        });
    },
    submit() {
      this.$v.$touch();
      // Check if delivery date is missing
      if (!this.Delivery.deliveryDate) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Delivery date must be selected before saving.",
          });
        return;
      }
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        // this.Delivery.deliveryDate =
        console.log("this.Delivery", this.Delivery);
        // return false;
        return deliveryService
          .create(this.Delivery)
          .then((result) => {
            if (result.status) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "Delivery is created",
                });
              this.empty();
              this.loading = false;
              this.$emit("submit");
            }
          })
          .catch((err) => {
            // console.log("error", err);
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err.data.message,
              });
          });
      }
    },
  },
};
</script>

<style scoped>
.delivery-dialog-card {
  border-radius: 20px;
  overflow: hidden;
  background: #f8fafc;
}

.delivery-dialog-header {
  padding: 18px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.delivery-dialog-title {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.15;
}

.delivery-dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 3px;
}

.delivery-date-card,
.form-card,
.day-delivery-card {
  border-radius: 18px;
  background: #ffffff;
  padding: 16px;
  border: 1px solid #e2e8f0 !important;
}

.section-title-row {
  display: flex;
  align-items: center;
}

.section-title {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

.section-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.day-delivery-card {
  min-height: 100%;
}

.delivery-existing-row {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-bottom: 8px;
  background: #f8fafc;
}

.empty-delivery-box {
  min-height: 140px;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  background: #f8fafc;
}

.delivery-actions {
  background: #ffffff;
  padding: 12px 16px;
}

@media (max-width: 600px) {
  .delivery-dialog-title {
    font-size: 20px;
  }

  .delivery-dialog-header {
    padding: 14px 16px;
  }

  .delivery-date-card,
  .form-card,
  .day-delivery-card {
    padding: 12px;
  }
}
</style>
